import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useBlocker } from "react-router-dom";
import PageContent from "../PageContent";
import useUserStore from "@/stores/user.store";
import useDashboardStore from "@/stores/dashboard.store";
import useSidebarStore from "@/stores/sidebar.store";
import {
  useDashboards,
  useSaveDashboardConfig,
  useUpdateDashboard,
  useDeleteDashboard,
  useShareDashboardWithSubTenants,
  usePublishDashboard,
} from "@/hooks/useDashboard";
import useTenant from "@/hooks/useTenant";
import {
  canManageSubTenantSharing,
  canPublishDashboard,
} from "./lib/dashboardSharing";
import { getDashboardIdentity } from "./lib/dashboardIdentity";
import { findOrganisationNameClash } from "./lib/dashboardNameClash";
import CloneDashboardDialog from "./components/CloneDashboardDialog";
import CopyWidgetToDashboardDialog from "./components/CopyWidgetToDashboardDialog";
import DashboardSelector from "./components/DashboardSelector";
import DashboardGrid from "./components/DashboardGrid";
import DashboardSkeleton from "./components/DashboardSkeleton";
import DashboardIdentityBanner from "./components/DashboardIdentityBanner";
import NewDashboardDialog from "./components/NewDashboardDialog";
import DashboardNameClashWarning, {
  NAME_CLASH_INPUT_CLASS,
} from "./components/DashboardNameClashWarning";
import {
  parseSkeletonLayoutSignature,
  persistDashboardSkeletonLayout,
  skeletonLayoutSignatureFromInstances,
} from "@/lib/dashboard-skeleton-cache";
import { parseTenantIdFromAccessToken } from "@/utils/auth";
import useUserTokenStore from "@/stores/user-token.store";
import { useCachedDashboardSkeletonLayout } from "@/hooks/useCachedDashboardSkeletonLayout";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
  Input,
  Label,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Plus,
  Save,
  X,
  Pencil,
  Copy,
  MoreVertical,
  Trash2,
  Edit,
  Share2,
  Lock,
  Send,
  EyeOff,
} from "lucide-react";
import { WidgetInstance } from "@/models/dashboard/Dashboard";
import type { WidgetConfig } from "@/models/dashboard/WidgetConfig";
import { toast } from "@capacit-com/lara-law-shared-ui";
import WidgetEditorPanel from "./components/WidgetEditorPanel";
import SaveAsTemplateDialog from "./components/SaveAsTemplateDialog";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Permission } from "@/lib/permissions/Permission.enum";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";

const DashboardView: FC = () => {
  const { me } = useUserStore();
  const { data: dashboards, isLoading: isDashboardsLoading } = useDashboards();
  const saveDashboardConfig = useSaveDashboardConfig();
  const updateDashboard = useUpdateDashboard();
  const deleteDashboard = useDeleteDashboard();
  const shareDashboard = useShareDashboardWithSubTenants();
  const publishDashboard = usePublishDashboard();
  const hasPermission = useHasPermission();

  // Releasing a dashboard only makes sense for a tenant that has sub-tenants (LAR-93).
  const { useQueryCurrent } = useTenant();
  const { data: currentTenant } = useQueryCurrent();

  // Check if user can edit organization dashboards
  const canEditOrgDashboards = hasPermission({
    allOf: [Permission.UpdateDashboardLayout],
  });

  const {
    selectedDashboardId,
    isEditMode,
    isDirty,
    localConfig,
    setSelectedDashboard,
    setEditMode,
    setLocalConfig,
    updateWidgets,
    removeWidget,
    clearDirty,
    editingWidgetId,
    isWidgetGalleryOpen,
    openWidgetGallery,
    beginWidgetEditing,
    endWidgetEditing,
  } = useDashboardStore();

  const token = useUserTokenStore((s) => s.token);
  const cacheTenantId = useMemo(
    () => parseTenantIdFromAccessToken(token),
    [token]
  );
  const cachedSkeletonWidgets =
    useCachedDashboardSkeletonLayout(selectedDashboardId);

  /** Stable layout fingerprint — matches cache module comparison (avoids effect churn). */
  const persistableLayoutSignature = useMemo(() => {
    if (!localConfig) return null;
    return skeletonLayoutSignatureFromInstances(localConfig.widgets);
  }, [localConfig]);

  // Persist snapshot derived only from persistableLayoutSignature (parsed in the effect) so the
  // payload always matches the dependency string — never a stale localConfig.widgets closure.
  useEffect(() => {
    if (!selectedDashboardId || isDirty || !me?.id) return;
    if (persistableLayoutSignature === null || !cacheTenantId) return;
    const widgets = parseSkeletonLayoutSignature(persistableLayoutSignature);
    if (widgets === null) return;
    persistDashboardSkeletonLayout(
      { userId: me.id, tenantId: cacheTenantId },
      selectedDashboardId,
      widgets
    );
  }, [
    selectedDashboardId,
    persistableLayoutSignature,
    isDirty,
    me?.id,
    cacheTenantId,
  ]);

  // Dialog states
  const [showNewDashboardDialog, setShowNewDashboardDialog] = useState(false);
  const [isCreatingOrgDashboard, setIsCreatingOrgDashboard] = useState(false);
  const [savingAsTemplateWidget, setSavingAsTemplateWidget] =
    useState<WidgetInstance | null>(null);
  const [showCloneDashboardDialog, setShowCloneDashboardDialog] =
    useState(false);
  const [copyingToOtherDashboardWidget, setCopyingToOtherDashboardWidget] =
    useState<WidgetInstance | null>(null);

  // Rename and delete dialogs
  const [showRenameDashboardDialog, setShowRenameDashboardDialog] =
    useState(false);
  const [renameDashboardName, setRenameDashboardName] = useState("");
  const [showDeleteDashboardDialog, setShowDeleteDashboardDialog] =
    useState(false);

  // Withdraw-share and unpublish confirmation dialogs
  const [showUnshareDashboardDialog, setShowUnshareDashboardDialog] =
    useState(false);
  const [showUnpublishDashboardDialog, setShowUnpublishDashboardDialog] =
    useState(false);

  // Widget editor panel (LAR-726). Its edits are already in the draft, so only
  // input it has not applied yet (a half-typed shortcut) needs a guard before
  // the panel closes or switches widget.
  const [widgetPanelSlot, setWidgetPanelSlot] = useState<HTMLElement | null>(
    null
  );
  const [hasUnappliedEditorInput, setHasUnappliedEditorInput] = useState(false);
  const [pendingEditorAction, setPendingEditorAction] = useState<
    (() => void) | null
  >(null);
  // Unapplied input belongs to the widget it was typed for; a panel that closed or
  // switched widget any other way (Annuller, delete, leaving edit mode) has none.
  const [inputWidgetId, setInputWidgetId] = useState(editingWidgetId);
  if (inputWidgetId !== editingWidgetId) {
    setInputWidgetId(editingWidgetId);
    setHasUnappliedEditorInput(false);
  }
  const isWidgetPanelOpen =
    isEditMode && (isWidgetGalleryOpen || editingWidgetId !== null);
  // The old configuration modal refused an empty name; Gem does now.
  const hasUnnamedWidget =
    localConfig?.widgets.some((w) => !w.name.trim()) ?? false;

  // The panel needs the width: the app sidebar folds away while it is open and
  // comes back when it closes, or when the user leaves the page.
  const setAppSidebarYielding = useSidebarStore((s) => s.setAppSidebarYielding);
  useEffect(() => {
    if (!isWidgetPanelOpen) return;
    setAppSidebarYielding(true);
    return () => setAppSidebarYielding(false);
  }, [isWidgetPanelOpen, setAppSidebarYielding]);

  const guardUnappliedEditorInput = (action: () => void) => {
    if (hasUnappliedEditorInput) {
      setPendingEditorAction(() => action);
    } else {
      action();
    }
  };

  // Unsaved changes warning dialog
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Block React Router navigation when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  // Select the default dashboard on load, and fall back to it when the persisted selection is
  // gone — a parent tenant can withdraw a shared dashboard (LAR-93) or an admin can delete one,
  // and the stored id would otherwise leave the page blank with no way back.
  useEffect(() => {
    if (!dashboards || dashboards.length === 0) return;
    if (
      selectedDashboardId &&
      dashboards.some((d) => d.id === selectedDashboardId)
    )
      return;

    const selectDefault = () => {
      const defaultDashboard =
        dashboards.find((d) => d.isDefault) || dashboards[0];
      setSelectedDashboard(defaultDashboard.id);
      setLocalConfig(defaultDashboard.config);
    };

    // The vanished dashboard might be one the user has unsaved edits open on - route through the
    // same "Ugemte ændringer" confirmation every other selection change in this file uses, instead
    // of silently discarding them.
    if (isDirty) {
      setPendingAction(() => selectDefault);
      setShowUnsavedChangesDialog(true);
    } else {
      selectDefault();
    }
  }, [
    dashboards,
    selectedDashboardId,
    setSelectedDashboard,
    setLocalConfig,
    isDirty,
  ]);

  // Update local config when dashboard changes
  useEffect(() => {
    if (dashboards && selectedDashboardId) {
      const dashboard = dashboards.find((d) => d.id === selectedDashboardId);
      if (dashboard && !isEditMode) {
        setLocalConfig(dashboard.config);
      }
    }
  }, [dashboards, selectedDashboardId, isEditMode, setLocalConfig]);

  // Show unsaved changes dialog when navigation is blocked.
  // Gated on isDirty to avoid re-opening the dialog after the user has
  // confirmed (clearDirty runs before blocker.proceed transitions state).
  useEffect(() => {
    if (blocker.state === "blocked" && isDirty) {
      setShowUnsavedChangesDialog(true);
    }
  }, [blocker.state, isDirty]);

  // Warn before leaving page with unsaved changes (browser refresh/close)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Intercept keyboard refresh shortcuts (Cmd/Ctrl+R, F5) and show the
  // branded confirmation before proceeding. Only attaches while dirty to
  // avoid unnecessary global listeners when there is nothing to protect.
  useEffect(() => {
    if (!isDirty) return;

    const handleRefreshShortcut = (e: globalThis.KeyboardEvent) => {
      const isCmdOrCtrlR =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "r";
      const isF5 = e.key === "F5";

      if (!isCmdOrCtrlR && !isF5) return;

      e.preventDefault();
      // discardChanges runs in handleConfirmDiscardChanges before this action,
      // so isDirty is false by the time the page unloads and beforeunload
      // does not fire a second native prompt.
      setPendingAction(() => () => {
        window.location.reload();
      });
      setShowUnsavedChangesDialog(true);
    };

    window.addEventListener("keydown", handleRefreshShortcut);
    return () => window.removeEventListener("keydown", handleRefreshShortcut);
  }, [isDirty]);

  const selectedDashboard = dashboards?.find(
    (d) => d.id === selectedDashboardId
  );

  const selectedIdentity = selectedDashboard
    ? getDashboardIdentity(selectedDashboard)
    : null;

  // LAR-280: a personal dashboard named like the organisation's is the one that gets mistaken for
  // it, so its edit banner points at the real one.
  const selectedPersonalNameClash =
    selectedIdentity?.kind === "personal" && dashboards
      ? findOrganisationNameClash(selectedDashboard?.name ?? "", dashboards)
      : null;

  const handleSelectDashboard = (id: string) => {
    // The dropdown reports a click on the row that is already checked too (the old select did not);
    // treating it as a switch would offer to discard unsaved changes for nothing.
    if (id === selectedDashboardId) return;

    const select = () => {
      setSelectedDashboard(id);
      const dashboard = dashboards?.find((d) => d.id === id);
      if (dashboard) {
        setLocalConfig(dashboard.config);
      }
      setEditMode(false);
      clearDirty();
    };

    if (isDirty) {
      setPendingAction(() => select);
      setShowUnsavedChangesDialog(true);
    } else {
      select();
    }
  };

  const handleSave = async () => {
    if (!selectedDashboardId || !localConfig || !selectedDashboard) return;

    try {
      await saveDashboardConfig.mutateAsync({
        dashboardId: selectedDashboardId,
        name: selectedDashboard.name,
        config: localConfig,
      });
      clearDirty();
      setEditMode(false);
      toast.success("Dashboard gemt", {
        description: "Dine ændringer er blevet gemt.",
      });
    } catch (e) {
      logger.error("Error saving dashboard", {
        operation: "saveDashboard",
        error: e,
        dashboardId: selectedDashboardId,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved gemning af dashboard.",
      });
    }
  };

  // Centralised discard so confirming "Kassér ændringer" always reverts the
  // dashboard regardless of which side-effect (dialog, navigation, reload)
  // triggered the prompt.
  const discardChanges = useCallback(() => {
    if (selectedDashboard) {
      setLocalConfig(selectedDashboard.config);
    }
    clearDirty();
    setEditMode(false);
  }, [selectedDashboard, setLocalConfig, clearDirty, setEditMode]);

  const handleDiscard = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      discardChanges();
    }
  };

  const handleConfirmDiscardChanges = () => {
    discardChanges();
    if (pendingAction) {
      // A nav blocker can also be raised while the dialog is open (e.g.
      // browser back/forward). The user's explicit intent here is the
      // pendingAction, so cancel the stray navigation instead of leaving
      // the blocker hanging.
      if (blocker.state === "blocked") {
        blocker.reset();
      }
      pendingAction();
      setPendingAction(null);
    } else if (blocker.state === "blocked") {
      blocker.proceed();
    }
    setShowUnsavedChangesDialog(false);
  };

  const handleCancelDiscardChanges = () => {
    setPendingAction(null);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setShowUnsavedChangesDialog(false);
  };

  const handleOpenCreateOrgDashboard = () => {
    if (isDirty) {
      setPendingAction(() => () => {
        setIsCreatingOrgDashboard(true);
        setShowNewDashboardDialog(true);
      });
      setShowUnsavedChangesDialog(true);
    } else {
      setIsCreatingOrgDashboard(true);
      setShowNewDashboardDialog(true);
    }
  };

  const handleOpenCreatePersonalDashboard = () => {
    if (isDirty) {
      setPendingAction(() => () => {
        setIsCreatingOrgDashboard(false);
        setShowNewDashboardDialog(true);
      });
      setShowUnsavedChangesDialog(true);
    } else {
      setIsCreatingOrgDashboard(false);
      setShowNewDashboardDialog(true);
    }
  };

  const handleOpenWidgetGallery = () => {
    if (!isEditMode) setEditMode(true);
    guardUnappliedEditorInput(openWidgetGallery);
  };

  const handleCloseWidgetPanel = () =>
    guardUnappliedEditorInput(endWidgetEditing);

  const handleLayoutChange = (widgets: WidgetInstance[]) => {
    updateWidgets(widgets);
  };

  const handleWidgetDelete = (widgetId: string) => {
    removeWidget(widgetId);
  };

  const handleWidgetEdit = (widgetId: string) => {
    if (widgetId === editingWidgetId) return;
    guardUnappliedEditorInput(() => beginWidgetEditing(widgetId));
  };

  const handleWidgetConfigUpdate = (widgetId: string, config: WidgetConfig) => {
    const { updateWidget } = useDashboardStore.getState();
    updateWidget(widgetId, { config });
  };

  const handleWidgetSaveAsTemplate = (widgetId: string) => {
    // Templates are readable by every authenticated user in the tenant, with no link back to the
    // dashboard they came from - saving one from a still-private draft would hand its content to
    // everyone the draft itself is hidden from (LAR-93).
    if (
      selectedDashboard?.isOrganizationDashboard &&
      !selectedDashboard.isPublished
    ) {
      toast.error("Kan ikke gemme som skabelon", {
        description:
          "Dashboardet er en kladde. Publicér det, før du gemmer en widget som skabelon.",
      });
      return;
    }

    const widget = localConfig?.widgets.find((w) => w.id === widgetId);
    if (widget) {
      setSavingAsTemplateWidget(widget);
    }
  };

  const handleWidgetCopyToOtherDashboard = (widgetId: string) => {
    const widget = localConfig?.widgets.find((w) => w.id === widgetId);
    if (widget) {
      setCopyingToOtherDashboardWidget(widget);
    }
  };

  const handleOpenRenameDashboard = () => {
    if (selectedDashboard) {
      setRenameDashboardName(selectedDashboard.name);
      setShowRenameDashboardDialog(true);
    }
  };

  const handleRenameDashboard = async () => {
    if (
      !selectedDashboardId ||
      !selectedDashboard ||
      !renameDashboardName.trim()
    )
      return;

    try {
      await updateDashboard.mutateAsync({
        id: selectedDashboardId,
        request: {
          name: renameDashboardName,
          configJson: JSON.stringify(selectedDashboard.config),
        },
      });
      setShowRenameDashboardDialog(false);
      setRenameDashboardName("");
      toast.success("Dashboard omdøbt", {
        description: `Dashboard er nu omdøbt til "${renameDashboardName}".`,
      });
    } catch (e) {
      logger.error("Error renaming dashboard", {
        operation: "renameDashboard",
        error: e,
        dashboardId: selectedDashboardId,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved omdøbning af dashboard.",
      });
    }
  };

  const handleOpenDeleteDashboard = () => {
    setShowDeleteDashboardDialog(true);
  };

  const handleDeleteDashboard = async () => {
    if (!selectedDashboardId || !selectedDashboard) return;

    try {
      await deleteDashboard.mutateAsync(selectedDashboardId);
      setShowDeleteDashboardDialog(false);

      if (dashboards && dashboards.length > 1) {
        const remainingDashboards = dashboards.filter(
          (d) => d.id !== selectedDashboardId
        );
        const nextDashboard = remainingDashboards[0];
        setSelectedDashboard(nextDashboard.id);
        setLocalConfig(nextDashboard.config);
      } else {
        setSelectedDashboard(null);
        setLocalConfig(null);
      }

      toast.success("Dashboard slettet", {
        description: `Dashboard "${selectedDashboard.name}" er blevet slettet.`,
      });
    } catch (e) {
      logger.error("Error deleting dashboard", {
        operation: "deleteDashboard",
        error: e,
        dashboardId: selectedDashboardId,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved sletning af dashboard.",
      });
    }
  };

  const canPublishSelectedDashboard = canPublishDashboard(selectedDashboard, {
    canEditOrgDashboards,
  });

  const handleSetDashboardPublished = async (isPublished: boolean) => {
    if (!selectedDashboardId || !selectedDashboard) return;

    try {
      await publishDashboard.mutateAsync({
        id: selectedDashboardId,
        request: { isPublished },
      });
      setShowUnpublishDashboardDialog(false);

      toast.success(
        isPublished ? "Dashboard publiceret" : "Publicering fjernet",
        {
          description: isPublished
            ? `"${selectedDashboard.name}" er nu synligt for alle i organisationen.`
            : `"${selectedDashboard.name}" er nu en kladde, som kun administratorer kan se.`,
        }
      );
    } catch (e) {
      logger.error("Error changing dashboard publish state", {
        operation: "publishDashboard",
        error: e,
        dashboardId: selectedDashboardId,
        isPublished,
      });
      toast.error("Fejl", {
        description: isPublished
          ? "Der opstod en fejl ved publicering af dashboard."
          : "Der opstod en fejl ved fjernelse af publicering.",
      });
    }
  };

  const canShareSelectedDashboard = canManageSubTenantSharing(
    selectedDashboard,
    {
      isParentTenant: currentTenant?.isParentTenant ?? false,
      canEditOrgDashboards,
    }
  );

  const handleSetDashboardShared = async (isShared: boolean) => {
    if (!selectedDashboardId || !selectedDashboard) return;

    try {
      await shareDashboard.mutateAsync({
        id: selectedDashboardId,
        request: { isShared },
      });
      setShowUnshareDashboardDialog(false);

      toast.success(isShared ? "Dashboard delt" : "Deling fjernet", {
        description: isShared
          ? `"${selectedDashboard.name}" er nu tilgængeligt for underorganisationer.`
          : `"${selectedDashboard.name}" er ikke længere tilgængeligt for underorganisationer.`,
      });
    } catch (e) {
      logger.error("Error changing dashboard sharing", {
        operation: "shareDashboardWithSubTenants",
        error: e,
        dashboardId: selectedDashboardId,
        isShared,
      });
      toast.error("Fejl", {
        description: isShared
          ? "Der opstod en fejl ved deling af dashboard."
          : "Der opstod en fejl ved fjernelse af deling.",
      });
    }
  };

  const canModifyDashboard =
    selectedDashboard &&
    (!selectedDashboard.isOrganizationDashboard || canEditOrgDashboards) &&
    !selectedDashboard.isFromParentTenant;

  const renameNameClash =
    showRenameDashboardDialog && selectedDashboard && dashboards
      ? findOrganisationNameClash(renameDashboardName, dashboards, {
          excludeId: selectedDashboard.id,
        })
      : null;

  if (isDashboardsLoading) {
    return (
      <PageContent title="Dashboard">
        <DashboardSkeleton cachedWidgets={cachedSkeletonWidgets} />
      </PageContent>
    );
  }

  return (
    <PageContent title="Dashboard">
      {/* The widget editor panel runs the page's full height beside the header, banner and
          grid (LAR-726). Below md the page scrolls as one column with the panel last, so the
          grid is not squeezed under a wrapped header. */}
      <div
        className={cn(
          "flex h-full flex-col animate-in fade-in duration-300 md:flex-row",
          isWidgetPanelOpen && "max-md:overflow-y-auto"
        )}
      >
        <div
          className={cn(
            "flex min-w-0 flex-col md:min-h-0 md:flex-1",
            !isWidgetPanelOpen && "min-h-0 flex-1"
          )}
        >
          {/* Header: the dashboard's name and whose it is on the left (LAR-661), actions on the right */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4 border-b">
            {dashboards && (
              <DashboardSelector
                dashboards={dashboards}
                selectedId={selectedDashboardId}
                onSelect={handleSelectDashboard}
                onCreateOrgDashboard={handleOpenCreateOrgDashboard}
                isEditMode={isEditMode}
              />
            )}

            <div className="flex items-center gap-2 flex-wrap justify-end">
              {canPublishSelectedDashboard &&
                !isEditMode &&
                selectedDashboard?.hasUnpublishedChanges && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetDashboardPublished(true)}
                    disabled={publishDashboard.isPending}
                    id="publish-changes-button"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Publicér ændringer
                  </Button>
                )}

              {canModifyDashboard && !isEditMode && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      id="dashboard-actions-menu"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleOpenRenameDashboard}
                      id="rename-dashboard-action"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Omdøb dashboard
                    </DropdownMenuItem>
                    {canPublishSelectedDashboard && (
                      <>
                        <DropdownMenuSeparator />
                        {selectedDashboard.isPublished ? (
                          <>
                            {selectedDashboard.hasUnpublishedChanges && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleSetDashboardPublished(true)
                                }
                                id="republish-dashboard-action"
                                disabled={publishDashboard.isPending}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Publicér ændringer
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() =>
                                setShowUnpublishDashboardDialog(true)
                              }
                              id="unpublish-dashboard-action"
                              disabled={publishDashboard.isPending}
                            >
                              <EyeOff className="h-4 w-4 mr-2" />
                              Gør til kladde
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleSetDashboardPublished(true)}
                            id="publish-dashboard-action"
                            disabled={publishDashboard.isPending}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Publicér til organisationen
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    {canShareSelectedDashboard && (
                      <>
                        <DropdownMenuSeparator />
                        {selectedDashboard.isSharedWithSubTenants ? (
                          <DropdownMenuItem
                            onClick={() => setShowUnshareDashboardDialog(true)}
                            id="unshare-dashboard-action"
                            disabled={shareDashboard.isPending}
                          >
                            <Lock className="h-4 w-4 mr-2" />
                            Fjern deling med underorganisationer
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleSetDashboardShared(true)}
                            id="share-dashboard-action"
                            disabled={shareDashboard.isPending}
                          >
                            <Share2 className="h-4 w-4 mr-2" />
                            Del med underorganisationer
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleOpenDeleteDashboard}
                      id="delete-dashboard-action"
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Slet dashboard
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenCreatePersonalDashboard}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nyt dashboard
              </Button>

              {dashboards && dashboards.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (isDirty) {
                      setPendingAction(() => () => {
                        setShowCloneDashboardDialog(true);
                      });
                      setShowUnsavedChangesDialog(true);
                    } else {
                      setShowCloneDashboardDialog(true);
                    }
                  }}
                  disabled={isEditMode}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Klon dashboard
                </Button>
              )}

              {isEditMode ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenWidgetGallery}
                    aria-expanded={isWidgetGalleryOpen}
                    aria-controls={
                      isWidgetGalleryOpen ? "widget-editor-panel" : undefined
                    }
                    id="add-widget-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Tilføj widget
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDiscard}>
                    <X className="h-4 w-4 mr-2" />
                    Annuller
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      guardUnappliedEditorInput(() => void handleSave())
                    }
                    // The editor shows "Giv widgetten et navn." beside the empty field.
                    disabled={
                      !isDirty ||
                      saveDashboardConfig.isPending ||
                      hasUnnamedWidget
                    }
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Gem
                  </Button>
                </>
              ) : (
                // Personal dashboards, and the tenant's own organisation dashboards for those who may
                // edit them. A dashboard from the parent organisation has its banner instead.
                canModifyDashboard && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(true)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Rediger
                  </Button>
                )
              )}
            </div>
          </div>

          {selectedIdentity && (
            <DashboardIdentityBanner
              identity={selectedIdentity}
              isEditMode={isEditMode}
              organisationNameClash={selectedPersonalNameClash}
              onOpenDashboard={handleSelectDashboard}
            />
          )}

          {/* Dashboard Content */}
          <div
            className={cn(
              "themed-scrollbar scrollbar-gutter-stable p-4 md:min-h-0 md:flex-1 md:overflow-auto",
              !isWidgetPanelOpen && "min-h-0 flex-1 overflow-auto"
            )}
          >
            {localConfig ? (
              localConfig.widgets.length > 0 ? (
                <DashboardGrid
                  widgets={localConfig.widgets}
                  isEditMode={isEditMode}
                  editingWidgetId={isWidgetPanelOpen ? editingWidgetId : null}
                  onLayoutChange={handleLayoutChange}
                  onWidgetConfigUpdate={handleWidgetConfigUpdate}
                  onWidgetEdit={handleWidgetEdit}
                  onWidgetDelete={handleWidgetDelete}
                  onWidgetSaveAsTemplate={handleWidgetSaveAsTemplate}
                  onWidgetCopyToOtherDashboard={
                    handleWidgetCopyToOtherDashboard
                  }
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                  <p className="text-lg mb-4">
                    Dette dashboard har ingen widgets endnu.
                  </p>
                  {canModifyDashboard && !isWidgetPanelOpen && (
                    <Button onClick={handleOpenWidgetGallery}>
                      <Plus className="h-4 w-4 mr-2" />
                      Tilføj din første widget
                    </Button>
                  )}
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                <p className="text-lg mb-4">
                  Vælg eller opret et dashboard for at komme i gang.
                </p>
                <Button onClick={handleOpenCreatePersonalDashboard}>
                  <Plus className="h-4 w-4 mr-2" />
                  Opret dashboard
                </Button>
              </div>
            )}
          </div>
        </div>

        {isWidgetPanelOpen && (
          <div
            ref={setWidgetPanelSlot}
            className="shrink-0 border-t md:w-[380px] md:border-t-0 md:border-l 2xl:w-[440px]"
          >
            {widgetPanelSlot && (
              <WidgetEditorPanel
                container={widgetPanelSlot}
                onRequestClose={handleCloseWidgetPanel}
                onDraftStateChange={setHasUnappliedEditorInput}
              />
            )}
          </div>
        )}
      </div>

      {/* New Dashboard Dialog */}
      <NewDashboardDialog
        open={showNewDashboardDialog}
        onOpenChange={setShowNewDashboardDialog}
        dashboards={dashboards ?? []}
        initialIsOrganizationDashboard={isCreatingOrgDashboard}
      />

      {/* Leaving the editor with a half-typed shortcut */}
      <ConfirmationDialog
        isOpen={pendingEditorAction !== null}
        onClose={() => setPendingEditorAction(null)}
        onConfirm={() => {
          setHasUnappliedEditorInput(false);
          pendingEditorAction?.();
          setPendingEditorAction(null);
        }}
        title="Kassér ændringer?"
        description="Det, du har skrevet i widgetten, men ikke tilføjet, bliver ikke gemt."
        confirmText="Kassér"
        cancelText="Fortsæt redigering"
        variant="destructive"
        discard
      />

      {/* Save As Template Dialog */}
      <SaveAsTemplateDialog
        widget={savingAsTemplateWidget}
        open={savingAsTemplateWidget !== null}
        onOpenChange={(open) => !open && setSavingAsTemplateWidget(null)}
      />

      {/* Clone Dashboard Dialog */}
      {dashboards && (
        <CloneDashboardDialog
          open={showCloneDashboardDialog}
          onOpenChange={setShowCloneDashboardDialog}
          dashboards={dashboards}
          sourceDashboardId={selectedDashboardId}
        />
      )}

      {/* Copy Widget To Another Dashboard Dialog */}
      <CopyWidgetToDashboardDialog
        widget={copyingToOtherDashboardWidget}
        sourceDashboardId={selectedDashboardId}
        open={copyingToOtherDashboardWidget !== null}
        onOpenChange={(open) => !open && setCopyingToOtherDashboardWidget(null)}
      />

      {/* Rename Dashboard Dialog */}
      <FormDialog
        open={showRenameDashboardDialog}
        onOpenChange={(open) => {
          setShowRenameDashboardDialog(open);
          if (!open) {
            setRenameDashboardName("");
          }
        }}
        isDirty={
          showRenameDashboardDialog &&
          renameDashboardName !== (selectedDashboard?.name ?? "")
        }
        id="rename-dashboard-dialog"
      >
        <DialogHeader>
          <DialogTitle>Omdøb dashboard</DialogTitle>
          <DialogDescription>
            Indtast et nyt navn til dit dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 gap-2 flex flex-col">
          <Label htmlFor="rename-dashboard-name">Navn</Label>
          <Input
            id="rename-dashboard-name"
            value={renameDashboardName}
            onChange={(e) => setRenameDashboardName(e.target.value)}
            placeholder="Dashboard navn"
            className={cn(renameNameClash && NAME_CLASH_INPUT_CLASS)}
          />
          <DashboardNameClashWarning
            clash={renameNameClash}
            isOrganizationDashboard={
              selectedDashboard?.isOrganizationDashboard ?? false
            }
          />
        </div>
        <DialogFooter>
          <FormDialogCancel />
          <Button
            onClick={handleRenameDashboard}
            disabled={!renameDashboardName.trim() || updateDashboard.isPending}
            id="confirm-rename-dashboard"
          >
            {renameNameClash ? "Omdøb alligevel" : "Omdøb"}
          </Button>
        </DialogFooter>
      </FormDialog>

      {/* Delete Dashboard Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDashboardDialog}
        onClose={() => setShowDeleteDashboardDialog(false)}
        onConfirm={handleDeleteDashboard}
        title="Slet dashboard"
        description={`Er du sikker på, at du vil slette dashboard "${selectedDashboard?.name}"? Denne handling kan ikke fortrydes.`}
        confirmText="Slet dashboard"
        variant="destructive"
      />

      {/* Unpublish Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showUnpublishDashboardDialog}
        onClose={() => setShowUnpublishDashboardDialog(false)}
        onConfirm={() => handleSetDashboardPublished(false)}
        confirmPending={publishDashboard.isPending}
        title="Gør til kladde"
        description={
          selectedDashboard?.isSharedWithSubTenants
            ? `"${selectedDashboard?.name}" bliver skjult for alle andre end administratorer, og delingen med underorganisationer fjernes samtidig.`
            : `"${selectedDashboard?.name}" bliver skjult for alle andre end administratorer.`
        }
        confirmText="Gør til kladde"
        variant="destructive"
      />

      {/* Withdraw-share Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showUnshareDashboardDialog}
        onClose={() => setShowUnshareDashboardDialog(false)}
        onConfirm={() => handleSetDashboardShared(false)}
        confirmPending={shareDashboard.isPending}
        title="Fjern deling"
        description={`Er du sikker på, at du vil fjerne delingen af "${selectedDashboard?.name}"? Underorganisationer mister adgang til dashboardet, men deres egne kopier bevares.`}
        confirmText="Fjern deling"
        variant="destructive"
      />

      {/* Unsaved Changes Warning Dialog */}
      <ConfirmationDialog
        isOpen={showUnsavedChangesDialog}
        onClose={handleCancelDiscardChanges}
        onConfirm={handleConfirmDiscardChanges}
        title="Kassér ændringer?"
        description="Det, du har skrevet, bliver ikke gemt."
        confirmText="Kassér"
        cancelText="Fortsæt redigering"
        variant="destructive"
        discard
      />
    </PageContent>
  );
};

export default DashboardView;
